import * as Y from "yjs"
import {
  Awareness,
  applyAwarenessUpdate,
  encodeAwarenessUpdate,
  removeAwarenessStates,
} from "y-protocols/awareness"
import {
  writeSyncStep1,
  writeUpdate,
  readSyncMessage,
  messageYjsSyncStep1,
  messageYjsSyncStep2,
} from "y-protocols/sync"
import * as encoding from "lib0/encoding"
import * as decoding from "lib0/decoding"

// A binary pub/sub channel between the peers in a session. `broadcast` sends a
// message to every OTHER peer (never echoed to the sender); `subscribe`
// registers a handler for messages from other peers. The Daily implementation
// lives in daily-transport.ts; tests use an in-memory bus. Keeping the provider
// transport-agnostic is what lets us prove convergence without a real browser.
export interface Transport {
  broadcast(bytes: Uint8Array): void
  // `fromId` identifies the sending peer (the Daily session id), so the provider
  // can reciprocate a fresh peer's sync handshake even after it has already
  // synced with someone else. The in-memory test bus passes the sender's port id.
  subscribe(handler: (bytes: Uint8Array, fromId: string) => void): () => void
}

const MESSAGE_SYNC = 0
const MESSAGE_AWARENESS = 1
const MESSAGE_QUERY_AWARENESS = 2

interface LiveYjsProviderOptions {
  // How often to re-announce our state vector so dropped updates self-heal.
  // Daily's app-message channel is best-effort (and we chunk large messages, so
  // a single lost chunk drops a whole Yjs update); a periodic sync step 1 lets
  // the CRDT reconcile. Set to 0 to disable (tests drive sync manually).
  resyncIntervalMs?: number
}

// Syncs a Y.Doc + Awareness across peers over an arbitrary {@link Transport},
// implementing the standard y-protocols sync + awareness handshake in a
// serverless, peer-to-peer (broadcast) topology. Each peer answers another
// peer's sync step 1 with its own state, so two peers converge after a single
// round-trip; periodic resyncs heal anything the lossy transport dropped.
export class LiveYjsProvider {
  readonly doc: Y.Doc
  readonly awareness: Awareness
  private readonly transport: Transport
  private readonly unsubscribe: () => void
  private resyncTimer: ReturnType<typeof setInterval> | null = null
  private syncedCallbacks: Array<() => void> = []
  // Peers (by fromId) whose sync step 1 we've already reciprocated, so we do it
  // once per peer (no infinite ping-pong) yet still reciprocate a NEW/reconnected
  // peer even after we've synced with someone else.
  private reciprocatedPeers = new Set<string>()
  synced = false

  constructor(
    doc: Y.Doc,
    awareness: Awareness,
    transport: Transport,
    options: LiveYjsProviderOptions = {},
  ) {
    this.doc = doc
    this.awareness = awareness
    this.transport = transport

    this.handleDocUpdate = this.handleDocUpdate.bind(this)
    this.handleAwarenessUpdate = this.handleAwarenessUpdate.bind(this)
    this.handleMessage = this.handleMessage.bind(this)

    this.doc.on("update", this.handleDocUpdate)
    this.awareness.on("update", this.handleAwarenessUpdate)
    this.unsubscribe = this.transport.subscribe(this.handleMessage)

    // Announce ourselves: ask peers for their state and share our awareness.
    this.requestSync()

    const interval = options.resyncIntervalMs ?? 5000
    if (interval > 0) {
      this.resyncTimer = setInterval(() => this.requestSync(), interval)
    }
  }

  // Broadcast a sync step 1 (our state vector) plus an awareness query. Called on
  // start, on a timer, and whenever we want to reconcile (e.g. a peer joins).
  requestSync(): void {
    const syncEncoder = encoding.createEncoder()
    encoding.writeVarUint(syncEncoder, MESSAGE_SYNC)
    writeSyncStep1(syncEncoder, this.doc)
    this.transport.broadcast(encoding.toUint8Array(syncEncoder))

    const queryEncoder = encoding.createEncoder()
    encoding.writeVarUint(queryEncoder, MESSAGE_QUERY_AWARENESS)
    this.transport.broadcast(encoding.toUint8Array(queryEncoder))

    this.broadcastAwareness([this.doc.clientID])
  }

  onSynced(callback: () => void): void {
    if (this.synced) {
      callback()
    } else {
      this.syncedCallbacks.push(callback)
    }
  }

  destroy(): void {
    if (this.resyncTimer !== null) {
      clearInterval(this.resyncTimer)
      this.resyncTimer = null
    }
    // Tell peers we're gone (drops our cursor) before we stop listening.
    removeAwarenessStates(this.awareness, [this.doc.clientID], "provider destroy")
    this.doc.off("update", this.handleDocUpdate)
    this.awareness.off("update", this.handleAwarenessUpdate)
    this.unsubscribe()
    this.syncedCallbacks = []
  }

  // Local doc edit → broadcast it. Updates that we applied FROM a peer carry
  // `this` as their origin (see handleMessage); skip those so we don't echo.
  private handleDocUpdate(update: Uint8Array, origin: unknown): void {
    if (origin === this) return
    const encoder = encoding.createEncoder()
    encoding.writeVarUint(encoder, MESSAGE_SYNC)
    writeUpdate(encoder, update)
    this.transport.broadcast(encoding.toUint8Array(encoder))
  }

  private handleAwarenessUpdate(
    { added, updated, removed }: { added: number[]; updated: number[]; removed: number[] },
    origin: unknown,
  ): void {
    if (origin === this) return
    this.broadcastAwareness([...added, ...updated, ...removed])
  }

  private broadcastAwareness(clients: number[]): void {
    // encodeAwarenessUpdate dereferences each client's `meta` entry, which only
    // exists once that client has set state at least once. Filtering by `meta`
    // (not `states`) keeps removals encodable — a removed client keeps its meta
    // entry with a bumped clock — while skipping our own client before its
    // cursor is initialized (e.g. the constructor's first sync).
    const known = clients.filter((id) => this.awareness.meta.has(id))
    if (known.length === 0) return
    const encoder = encoding.createEncoder()
    encoding.writeVarUint(encoder, MESSAGE_AWARENESS)
    encoding.writeVarUint8Array(encoder, encodeAwarenessUpdate(this.awareness, known))
    this.transport.broadcast(encoding.toUint8Array(encoder))
  }

  private handleMessage(bytes: Uint8Array, fromId = "unknown"): void {
    const decoder = decoding.createDecoder(bytes)
    const messageType = decoding.readVarUint(decoder)

    switch (messageType) {
      case MESSAGE_SYNC: {
        const encoder = encoding.createEncoder()
        encoding.writeVarUint(encoder, MESSAGE_SYNC)
        // Applies remote step 2 / updates with `this` as the transaction origin
        // (so handleDocUpdate skips them); writes a step 2 reply when the peer
        // sent us a step 1.
        const syncMessageType = readSyncMessage(decoder, encoder, this.doc, this)
        if (encoding.length(encoder) > 1) {
          this.transport.broadcast(encoding.toUint8Array(encoder))
        }
        // A peer's step 1 only makes us send OUR state (step 2); it does not make
        // us ask for THEIRS. So reciprocate with our own step 1 to pull any state
        // the peer already had — converging a fresh join in one round trip. Gate
        // on a PER-PEER set (not the global `synced` latch): reciprocate the first
        // step 1 from each distinct peer, so a peer that RECONNECTS with offline
        // edits is pulled immediately even though we already synced with someone.
        // One reciprocation per peer also prevents an infinite step-1 ping-pong.
        if (syncMessageType === messageYjsSyncStep1 && !this.reciprocatedPeers.has(fromId)) {
          this.reciprocatedPeers.add(fromId)
          const reply = encoding.createEncoder()
          encoding.writeVarUint(reply, MESSAGE_SYNC)
          writeSyncStep1(reply, this.doc)
          this.transport.broadcast(encoding.toUint8Array(reply))
        }
        if (syncMessageType === messageYjsSyncStep2) {
          this.markSynced()
        }
        break
      }
      case MESSAGE_AWARENESS: {
        applyAwarenessUpdate(this.awareness, decoding.readVarUint8Array(decoder), this)
        break
      }
      case MESSAGE_QUERY_AWARENESS: {
        this.broadcastAwareness(Array.from(this.awareness.getStates().keys()))
        break
      }
    }
  }

  private markSynced(): void {
    if (this.synced) return
    this.synced = true
    const callbacks = this.syncedCallbacks
    this.syncedCallbacks = []
    for (const callback of callbacks) callback()
  }
}
